import type { KeyframeRule, StyleRule } from '@archipro-design/aria';
import {
    boxClassName,
    videoPlayerClassName,
    pxToRem,
    pxArrayToRem,
    labelClassName,
} from '@archipro-design/aria';
import { dialogSlotClassNames } from '@fluentui/react-northstar';
import { svgIconClassName } from '@archipro-design/icons';

export const DirectoryHeroContainer: StyleRule = () => ({
    position: 'relative',
    width: '100%',
    height: 'auto',
});

export const Frame: StyleRule = () => ({
    position: 'relative',
    width: '100%',
    margin: 'auto',
});

export const Cover: StyleRule<{ playing: boolean }> = ({ theme, playing }) => ({
    position: 'absolute',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)',
    willChange: 'transform, opacity',
    opacity: playing ? 0 : 1,
    transition: 'opacity 200ms linear 200ms',
    [`&::after`]: {
        content: '" "',
        display: `block`,
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        background: theme.siteVariables.colors.primitive.black,
    },
    [theme.screen.tablet]: {
        width: '100%',
        height: 'auto',
        position: 'absolute',
        [`& > img`]: {
            width: '100%',
            height: 'auto',
        },
    },
});

export const Player: StyleRule = () => ({
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: 'auto',
});

export const VideoAspect: StyleRule = () => ({
    position: 'relative',
    paddingTop: '56.25%',
    [`& .${videoPlayerClassName}`]: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        [`& .${boxClassName}`]: {
            position: 'absolute',
            overflow: 'hidden',
            [`& svg`]: {
                transition: 'none',
            },
        },
    },
    overflow: 'hidden',
});

export const DirectoryHeroOverlay: StyleRule<{ autoplay: boolean }> = ({
    theme,
    autoplay,
}) => ({
    position: 'absolute',
    display: 'flex',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    padding: pxToRem(76),
    zIndex: autoplay
        ? theme.siteVariables.zIndexes.foreground
        : theme.siteVariables.zIndexes.background,
});

export const Spacer: StyleRule = () => ({
    padding: pxArrayToRem([0, 50]),
    maxWidth: pxToRem(1920),
    margin: 'auto',
});

export const DirectoryHeroContent: StyleRule = () => ({
    flexBasis: pxToRem(680),
});

export const DirectoryHeroViewMore: StyleRule = ({
    scrollY,
    heroSize,
    screenSize,
}) => {
    const heightDiff =
        heroSize && heroSize.height > screenSize.height
            ? heroSize.height - screenSize.height
            : 0;

    const scrollMax = heightDiff + 60;

    return {
        justifySelf: 'center',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        transform: `translate(0, ${pxToRem(
            scrollY < scrollMax ? -scrollMax + scrollY : -40
        )})`,
        opacity: 1 - (scrollY < scrollMax ? scrollY : scrollMax) / scrollMax,
        fontSize: pxToRem(20),
        color: 'white',
        transitionProperty: 'transform, opacity',
        transitionDuration: '500ms',
        cursor: 'pointer',
        willChange: 'transform, opacity',
    };
};

export const UpDownMotionKeyframes: KeyframeRule = () => ({
    from: {
        transform: 'translateY(0)',
    },
    to: {
        transform: `translateY(${pxToRem(10)})`,
    },
});

export const DirectoryHeroViewMoreLabel: StyleRule = () => ({
    width: '100vw',
    margin: '0 auto',
    display: 'block',
    textAlign: 'center',
});

export const DirectoryHeroViewMoreIcon: StyleRule<{
    upDownMotionKeyframes: string;
}> = ({ upDownMotionKeyframes }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: pxToRem(10),

    [`& .${svgIconClassName}`]: {
        width: pxToRem(24),
        height: pxToRem(24),
        animation: `${upDownMotionKeyframes} 1s infinite alternate`,

        '> svg > path': {
            fill: '#ffffff',
        },
    },
});

export const title: StyleRule = ({ theme }) => ({
    lineHeight: 1,
    letterSpacing: pxToRem(-1.1),
    color: theme.siteVariables.colors.primitive.white,
    margin: 0,
    marginBottom: pxToRem(33),
    transform: `translateX(${pxToRem(-5)})`,

    [theme.screen.tablet]: {
        fontSize: pxToRem(110),
    },
});

export const Description: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.primitive.white,
    lineHeight: 1.4,
    maxWidth: pxToRem(540),
    marginBottom: pxToRem(51),
});

export const Actions: StyleRule = () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(7),
});

export const VideoButton: StyleRule = () => ({
    width: pxToRem(208),
    height: pxToRem(48),
    padding: pxToRem(18),
    overflow: 'hidden',
    [`& .${labelClassName}`]: {
        textTransform: 'uppercase',
        letterSpacing: pxToRem(-0.23),
        whiteSpace: 'nowrap',
    },
});

export const PrimaryButton: StyleRule = ({ theme }) => ({
    width: pxToRem(208),
    height: pxToRem(48),
    padding: pxToRem(18),
    overflow: 'hidden',
    [`& .${labelClassName}`]: {
        textTransform: 'uppercase',
        letterSpacing: pxToRem(-0.23),
        whiteSpace: 'nowrap',
        color: theme.siteVariables.colors.primitive.white,
    },
    [theme.screen.tablet]: {
        border: `1px solid ${theme.siteVariables.colors.primitive.white}`,
    },
});

export const FullVideoModal: StyleRule = () => ({
    height: '100%',
    width: '100%',
    [`& > .${boxClassName}`]: {
        margin: 'auto',
        maxWidth: `min(100%, calc(160vh - ${pxToRem(120)}))`,
    },
    [`& .${dialogSlotClassNames.headerAction}`]: {
        outline: 'none',
        border: 'none',
        position: 'fixed',
        zIndex: 1,
        top: pxToRem(30),
        right: pxToRem(40),
        backgroundColor: `transparent`,
    },
});

export const FullVideoModalClose: StyleRule = ({ theme }) => ({
    opacity: 0.8,
    minWidth: pxToRem(22),
    minHeight: pxToRem(22),
    '&:hover': {
        opacity: 1,
    },
    '& svg': {
        fill: theme.siteVariables.colors.white['300'],
        width: pxToRem(22),
        height: pxToRem(22),
    },
});

export const VideoAspectMobile: StyleRule = () => ({
    position: 'relative',
    aspectRatio: '9 / 16',
    width: '100%',
    [`& .${videoPlayerClassName}`]: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        [`& .${boxClassName}`]: {
            position: 'absolute',
            overflow: 'hidden',
            [`& svg`]: {
                transition: 'none',
            },
        },
    },
    overflow: 'hidden',
});

export const BannerCentralContainer: StyleRule = () => ({
    aspectRatio: '9 / 16',
    width: '100%',
    position: 'absolute',
});

export const CoverMobile: StyleRule<{ playing: boolean }> = ({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    willChange: 'transform, opacity',
    transition: 'opacity 200ms linear 200ms',
    aspectRatio: '9 / 16',
    '& img': {
        position: 'relative',
        width: '100%',
        aspectRatio: '9 / 16',
    },
    [`&::after`]: {
        // a place holder  in content will make it render a black background
        // equals to content: " ";
        content: '" "',
        display: 'block',
        position: 'absolute',
        inset: 0,
        background: theme.siteVariables.colors.primitive.black,
    },
});

export const DirectoryHeroOverlayMobile: StyleRule<{ autoplay: boolean }> = ({
    theme,
    autoplay,
}) => ({
    position: 'absolute',
    display: 'flex',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    padding: pxToRem(18),
    zIndex: autoplay
        ? theme.siteVariables.zIndexes.foreground
        : theme.siteVariables.zIndexes.background,
});

export const PlayerMobile: StyleRule = () => ({
    position: 'absolute',
    left: '-50%',
    top: '50%',
    width: '100%',
    height: 'auto',
    transform: 'translate(50%, -50%)',
});

export const SpacerMobile: StyleRule = () => ({
    padding: pxArrayToRem([0, 0]),
    maxWidth: '100%',
    margin: 'auto',
});

export const titleMobile: StyleRule = ({ theme }) => ({
    lineHeight: 1,
    letterSpacing: pxToRem(-1.1),
    color: theme.siteVariables.colors.primitive.white,
    margin: 0,
    marginBottom: pxToRem(24),
    transform: `translateX(${pxToRem(-5)})`,
    fontSize: pxToRem(42),
    fontWeight: 400,
});

export const DescriptionMobile: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.primitive.white,
    lineHeight: 1.3,
    maxWidth: pxToRem(280),
    marginBottom: pxToRem(24),
});

export const VideoButtonMobile: StyleRule = () => ({
    width: pxToRem(220),
    height: pxToRem(40),
    padding: pxToRem(18),
    overflow: 'hidden',
    [`& .${labelClassName}`]: {
        textTransform: 'uppercase',
        letterSpacing: pxToRem(-0.23),
        whiteSpace: 'nowrap',
    },
});

export const PrimaryButtonMobile: StyleRule = ({ theme }) => ({
    width: pxToRem(223),
    height: pxToRem(40),
    padding: pxToRem(18),
    overflow: 'hidden',
    [`& .${labelClassName}`]: {
        textTransform: 'uppercase',
        letterSpacing: pxToRem(-0.23),
        whiteSpace: 'nowrap',
        color: theme.siteVariables.colors.primitive.black,
    },
    [theme.screen.tablet]: {
        border: `1px solid ${theme.siteVariables.colors.primitive.white}`,
    },
});

export const DirectoryHeroContentMobile: StyleRule = () => ({
    transform: `translateY(${pxToRem(-60)})`,
});

export const DirectoryHeroViewMoreMobile: StyleRule = ({ isLandscape }) => {
    return {
        justifySelf: 'center',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: isLandscape ? 'none' : pxToRem(50),
        top: isLandscape ? pxToRem(100) : 'none',
        fontSize: pxToRem(14),
        color: 'white',
        transitionProperty: 'transform, opacity',
        transitionDuration: '250ms',
        cursor: 'pointer',
        willChange: 'transform, opacity',
        opacity: 0,
        transform: isLandscape ? 'none' : `translateY(${pxToRem(-20)}})`,
    };
};

export const DirectoryHeroViewMoreIconMobile: StyleRule<{
    upDownMotionKeyframes: string;
}> = ({ upDownMotionKeyframes }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: pxToRem(4),
    [`& .${svgIconClassName}`]: {
        width: pxToRem(16),
        height: pxToRem(16),
        animation: `${upDownMotionKeyframes} 1s infinite alternate`,
        '> svg > path': {
            fill: '#ffffff',
        },
    },
});
